import { useEffect, useRef } from 'react';

const IDLE_TIMEOUT_MS = 5 * 60 * 1000; // 5 minutes
const SESSION_KEY = 'chatbot_nudge_shown';

/**
 * Fires onNudge() once per browser session after IDLE_TIMEOUT_MS of no
 * user activity (mouse, keyboard, scroll, touch). Uses sessionStorage to
 * ensure it only fires once per session.
 */
export const useIdleNudge = (onNudge: () => void): void => {
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const onNudgeRef = useRef(onNudge);

  // Keep ref current so timer closure always calls latest callback
  useEffect(() => {
    onNudgeRef.current = onNudge;
  }, [onNudge]);

  useEffect(() => {
    // Already shown this session — don't set up listeners at all
    if (sessionStorage.getItem(SESSION_KEY)) return;

    const handleActivity = () => {
      if (timerRef.current) clearTimeout(timerRef.current);
      timerRef.current = setTimeout(() => {
        // Double-check in case another tab set the key
        if (!sessionStorage.getItem(SESSION_KEY)) {
          sessionStorage.setItem(SESSION_KEY, 'true');
          onNudgeRef.current();
        }
      }, IDLE_TIMEOUT_MS);
    };

    const events = ['mousemove', 'keydown', 'scroll', 'touchstart'] as const;
    events.forEach((e) => {
      window.addEventListener(e, handleActivity, { passive: true });
    });

    // Start initial timer immediately (user may never move)
    handleActivity();

    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
      events.forEach((e) => {
        window.removeEventListener(e, handleActivity);
      });
    };
  }, []);
};
